import startCase from 'lodash/startCase';
import React from 'react';
import styled from 'styled-components';
import {
  ContentDetailsFragment,
  useGetContentDetailsQuery,
} from '../../graphql/types';
import useTracking from '../../hooks/useTracking';
import { EXOS_GRAY } from '../../theme';
import { edgesToNodes } from '../../utils/toEdges';
import {
  EventCategories,
  EventNames,
  EventProfileCardNames,
  EventSubCategories,
} from '../../utils/trackingEvents';
import LoadingPanel from '../LoadingPanel';
import Modal from '../Modal';
import Button, { ButtonVariant } from '../reusable/Button';
import { DataGrid } from '../reusable/DataGrid';
import Text, { TextColor, TextKind, textStyleFrom } from '../reusable/Text';
import { ContentViewer } from './ContentView';
import { cleanUpBrand, getAudioVideoContent, getOnDemandContent } from './util';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (
    id: string,
    subCategory: EventSubCategories,
    cardName?: EventProfileCardNames,
  ) => void;
  id: string | null;
}

const HtmlViewer = styled.div`
  ${textStyleFrom(TextKind.BodyS, TextColor.Primary)}
  h1 {
    ${textStyleFrom(TextKind.TitleM, TextColor.Primary)}
    font-size: 24px;
  }
  a {
    pointer-events: none;
    color: inherit;
    text-decoration: none;
    cursor: text;
  }
`;

const MetaDataContainer = styled.div`
  padding: 16px;
  background: ${EXOS_GRAY};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const MetaDataText = ({
  label,
  value,
}: {
  label: string;
  value?: string | null | false;
}) => {
  if (!value || value.length === 0) {
    return null;
  }

  return (
    <Text kind={TextKind.BodyS}>
      {label}:{' '}
      <Text kind={TextKind.BodyS} component="span" bold>
        {value || 'N/A'}
      </Text>
    </Text>
  );
};

const ContentMetadata = ({
  content,
}: {
  content: ContentDetailsFragment | null;
}) => {
  if (!content) {
    return null;
  }

  const genre =
    content.contentGenre?.parent?.name ?? content.contentGenre?.name;
  const subGenre = content.contentGenre?.parent
    ? content.contentGenre?.name
    : null;
  const equipment = content.contentEquipment.map(({ name }) => name).join(', ');

  const isOnDemand =
    content.__typename === 'Article' ||
    content.__typename === 'Video' ||
    content.__typename === 'Audio';

  const primaryComponent =
    isOnDemand && content.gameplanMetadata?.primaryComponent?.name;
  const primarySubcomponents =
    isOnDemand &&
    content.gameplanMetadata?.primarySubcomponents
      ?.map((node) => node?.name)
      .join(', ');
  const secondaryComponent =
    isOnDemand && content.gameplanMetadata?.secondaryComponent?.name;
  const secondarySubcomponents =
    isOnDemand &&
    content.gameplanMetadata?.secondarySubcomponents
      ?.map((node) => node?.name)
      .join(', ');
  const brand = isOnDemand && content.brand && cleanUpBrand(content.brand);

  const isExperiential = () => {
    if (isOnDemand) {
      return content.gameplanMetadata?.isExperiential ? 'Yes' : 'No';
    }
    return null;
  };

  return (
    <MetaDataContainer>
      <MetaDataText label="Primary Component" value={primaryComponent} />
      <MetaDataText
        label="Primary Subcomponents"
        value={primarySubcomponents}
      />
      <MetaDataText label="Secondary Component" value={secondaryComponent} />
      <MetaDataText
        label="Secondary Subcomponents"
        value={secondarySubcomponents}
      />
      <MetaDataText label="Brand" value={brand} />
      <MetaDataText label="Genre" value={genre} />
      <MetaDataText label="Subgenre" value={subGenre} />
      {isOnDemand && (
        <MetaDataText
          label="Intensity"
          value={content.intensity ? startCase(content.intensity) : null}
        />
      )}
      <MetaDataText
        label="Level of Difficulty"
        value={content.contentLevel?.name}
      />
      <MetaDataText label="Equipment" value={equipment} />
      <MetaDataText label="Time" value={content.contentTime?.name} />
      <MetaDataText label="Is Experiential" value={isExperiential()} />
      {isOnDemand && <MetaDataText label="Split" value={content.split?.name} />}
      {isOnDemand && (
        <MetaDataText
          label="Project"
          value={content.gameplanMetadata?.associatedProject?.name}
        />
      )}
    </MetaDataContainer>
  );
};

export const PreviewModal: React.FC<Props> = ({
  isOpen,
  onSelect,
  onClose,
  id,
}) => {
  const { track } = useTracking();
  const { data, loading } = useGetContentDetailsQuery({
    variables: { id: id || '' },
    skip: !id,
  });

  const trackingData = {
    card_name: EventProfileCardNames.ContentPreviewModal,
    event_category: EventCategories.Content,
    event_subcategory: EventSubCategories.Preview,
    content_id: id || undefined,
  };

  const closePreview = () => {
    onClose();
    track(EventNames.CloseModal, trackingData);
  };

  const content = getOnDemandContent(data?.content);
  const movementProgressions = getAudioVideoContent(
    data?.content,
  )?.movementProgressions;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="800px"
      trackingData={trackingData}
      testId="contentPreviewModal"
    >
      <Modal.Header>
        <Text kind={TextKind.TitleM}>Preview</Text>
      </Modal.Header>
      <Modal.Content style={{ height: 500 }}>
        {loading && (
          <LoadingPanel fullHeight noBorder testId="contentPreviewLoading" />
        )}
        {!loading && (
          <>
            <Text kind={TextKind.TitleXl}>{content?.title}</Text>
            <Text>{content?.author || 'Author: N/A'}</Text>
            <ContentMetadata content={content} />
            <ContentViewer content={content} />
            {content?.html && (
              <HtmlViewer
                data-testid="htmlContentPreview"
                // biome-ignore lint/security/noDangerouslySetInnerHtml: we control html content
                dangerouslySetInnerHTML={{ __html: content.html }}
              />
            )}
            <div>
              {movementProgressions &&
                (movementProgressions.edges?.length || 0) > 0 && (
                  <DataGrid
                    trackingData={trackingData}
                    rows={edgesToNodes(movementProgressions.edges)}
                    columns={[
                      {
                        field: 'description',
                        headerName: 'Movement',
                        flex: 1,
                        sortable: false,
                        resizable: false,
                        filterable: false,
                      },
                      {
                        field: 'repetitions',
                        headerName: 'Repetitions',
                        flex: 1,
                        sortable: false,
                        resizable: false,
                        filterable: false,
                      },
                    ]}
                  />
                )}
            </div>
          </>
        )}
      </Modal.Content>
      <Modal.Footer>
        <Button
          onClick={() =>
            onSelect(
              id ?? '',
              EventSubCategories.Preview,
              EventProfileCardNames.ContentPreviewModal,
            )
          }
          variant={ButtonVariant.Dark}
          testId="previewSelect"
        >
          Select
        </Button>
        <Button onClick={closePreview}>Back</Button>{' '}
      </Modal.Footer>
    </Modal>
  );
};
